import axios from "axios";
import { debounce } from "lodash";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";
import {
  COURTESY_PRODUCT_OPTIONS as PRODUCT_OPTIONS,
  COURTESY_DURATION_MODES as DURATION_MODES,
  COURTESY_JUSSUB_ALLOWED_DAYS,
  COURTESY_JUSSUB_DURATION_OPTIONS,
  daysUntilDate,
  addDaysToIso
} from "../../../constants/courtesy";
import { PlansAPI } from "../../../api/payments/plans/api";

const formatBrDate = iso => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};

export default function CourtesyModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  clientEmail,
  currentPlanName,
  useJusSub
}) {
  const [product, setProduct] = useState(null);
  const [durationMode, setDurationMode] = useState("plan");
  const [durationDays, setDurationDays] = useState("");
  const [endDate, setEndDate] = useState("");
  const [planMode, setPlanMode] = useState(useJusSub ? "other" : "same");
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [plans, setPlans] = useState([]);
  const [filteredPlans, setFilteredPlans] = useState([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);

  const availableDurationModes = useMemo(() => {
    if (useJusSub) {
      return DURATION_MODES.filter(mode => mode.value !== "plan");
    }
    return DURATION_MODES;
  }, [useJusSub]);

  const resetFields = () => {
    setProduct(null);
    setDurationMode(useJusSub ? "days" : "plan");
    setDurationDays("");
    setEndDate("");
    setPlanMode(useJusSub ? "other" : "same");
    setSelectedPlan(null);
  };

  useEffect(() => {
    if (isOpen) {
      resetFields();
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    let cancelled = false;

    const loadPlans = async () => {
      try {
        setIsLoadingPlans(true);

        if (useJusSub) {
          const response = await PlansAPI.list();
          const plansList = response.data?.content || [];
          if (!cancelled) {
            setPlans(plansList);
            setFilteredPlans(plansList.slice(0, 10));
          }
          return;
        }

        const response = await axios.get(
          process.env.REACT_APP_API_URL + "/subscription/plans_admin"
        );
        if (!cancelled && response.data?.plans) {
          setPlans(response.data.plans);
          setFilteredPlans(response.data.plans.slice(0, 10));
        }
      } catch (err) {
        // no fluxo JusSub o erro já é exibido via toast pela camada de API
        if (!cancelled && !useJusSub) {
          toast.error("Ocorreu um erro ao carregar os planos.");
        }
      } finally {
        if (!cancelled) setIsLoadingPlans(false);
      }
    };

    loadPlans();
    return () => {
      cancelled = true;
    };
  }, [isOpen, useJusSub]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedFilter = useCallback(
    debounce(inputValue => {
      if (inputValue) {
        setFilteredPlans(
          plans.filter(plan =>
            plan.name.toLowerCase().includes(inputValue.toLowerCase())
          )
        );
      } else {
        setFilteredPlans(plans.slice(0, 10));
      }
    }, 300),
    [plans]
  );

  const resolveActiveDays = () => {
    if (durationMode === "plan") {
      return null;
    }

    if (durationMode === "endDate") {
      const days = daysUntilDate(endDate);
      if (days == null || !Number.isFinite(days) || days < 1) {
        return { error: "Informe uma data de fim futura." };
      }
      if (useJusSub && !COURTESY_JUSSUB_ALLOWED_DAYS.includes(days)) {
        return {
          error:
            "A data de fim precisa resultar em um dos intervalos aceitos pelo JusSub (1, 7, 14, 30, 60, 90, 180 ou 365 dias)."
        };
      }
      return { days };
    }

    const days = Number(durationDays);
    if (!Number.isInteger(days) || days < 1) {
      return {
        error: "A duração deve ser um número inteiro maior ou igual a 1."
      };
    }
    if (useJusSub && !COURTESY_JUSSUB_ALLOWED_DAYS.includes(days)) {
      return {
        error:
          "Duração inválida. Selecione um dos intervalos aceitos pelo JusSub."
      };
    }
    return { days };
  };

  const confirmationSummary = useMemo(() => {
    const planLabel =
      planMode === "other" || useJusSub
        ? selectedPlan?.name || "plano selecionado"
        : currentPlanName
          ? `plano atual (${currentPlanName})`
          : "plano atual do cliente";

    let untilLabel = "conforme a duração do plano";
    if (durationMode === "endDate" && endDate) {
      untilLabel = `até ${formatBrDate(endDate)}`;
    } else if (durationMode === "days" && durationDays) {
      untilLabel = `por ${durationDays} dia(s) (até ${formatBrDate(
        addDaysToIso(durationDays)
      )})`;
    }

    return `O cliente receberá acesso de cortesia ao ${planLabel} ${untilLabel}. Isso libera o plano inteiro durante a vigência — não é crédito de uma funcionalidade isolada.`;
  }, [
    planMode,
    useJusSub,
    selectedPlan,
    currentPlanName,
    durationMode,
    endDate,
    durationDays
  ]);

  const handleConfirm = async () => {
    if (!product?.value) {
      toast.error("Por favor, selecione o tipo de liberação da cortesia.");
      return;
    }

    if ((planMode === "other" || useJusSub) && !selectedPlan?.id) {
      toast.error("Por favor, selecione o plano.");
      return;
    }

    const resolved = resolveActiveDays();
    if (resolved?.error) {
      toast.error(resolved.error);
      return;
    }

    if (
      !window.confirm(
        `${confirmationSummary}\n\nConfirma a criação desta cortesia?`
      )
    ) {
      return;
    }

    const data = {
      ReleaseCategory: product.value.trim().toLowerCase()
    };

    if (resolved?.days) {
      data.activeDays = resolved.days;
    }

    if (planMode === "other" || useJusSub) {
      data.planId = String(selectedPlan.id);
    }

    if (onConfirm) {
      await onConfirm(data);
    }
  };

  return (
    <Modal show={isOpen} onHide={onClose} centered>
      <Modal.Header className="border-bottom">
        <Modal.Title className="text-lg font-semibold text-gray-900">
          Criar cortesia
        </Modal.Title>
        <button type="button" className="close" onClick={onClose}>
          <i aria-hidden="true" className="ki ki-close"></i>
        </button>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted mb-4">
          O cliente receberá acesso de cortesia ao plano selecionado durante a
          vigência concedida. Isso libera o plano inteiro — para liberar só uma
          funcionalidade, use crédito gratuito em Benefícios.
        </p>

        <div className="mb-4">
          <label className="form-label">E-mail do cliente</label>
          <input disabled className="form-control" value={clientEmail || ""} />
        </div>

        <div className="mb-4">
          <label className="form-label">
            Tipo de liberação <span className="text-danger">*</span>
          </label>
          <CreatableSelect
            className="form-select"
            placeholder="Selecione ou digite o tipo de liberação"
            options={PRODUCT_OPTIONS}
            value={product}
            formatCreateLabel={inputValue => `Usar "${inputValue}"`}
            onChange={setProduct}
          />
        </div>

        <div className="mb-4">
          <label className="form-label">Plano</label>
          <div className="radio-list">
            {!useJusSub && (
              <>
                <label className="radio">
                  <input
                    type="radio"
                    name="planMode"
                    checked={planMode === "same"}
                    onChange={() => setPlanMode("same")}
                  />
                  <span></span>
                  Mesmo plano do cliente
                  {currentPlanName ? ` (${currentPlanName})` : ""}
                </label>
                <label className="radio">
                  <input
                    type="radio"
                    name="planMode"
                    checked={planMode === "other"}
                    onChange={() => setPlanMode("other")}
                  />
                  <span></span>
                  Selecionar outro plano
                </label>
              </>
            )}
          </div>
          {(planMode === "other" || useJusSub) && (
            <Select
              options={filteredPlans}
              value={selectedPlan}
              isLoading={isLoadingPlans}
              onChange={setSelectedPlan}
              onInputChange={debouncedFilter}
              placeholder="Selecione o plano.."
              getOptionLabel={option => option.name}
              getOptionValue={option => option.id}
              noOptionsMessage={() => <span>Sem resultados.</span>}
            />
          )}
        </div>

        <div className="mb-4">
          <div className="mb-6">
            <label className="form-label">Vigência</label>
            <select
              className="form-control"
              value={durationMode}
              onChange={e => {
                setDurationMode(e.target.value);
                setDurationDays("");
                setEndDate("");
              }}
            >
              {availableDurationModes.map(mode => (
                <option key={mode.value} value={mode.value}>
                  {mode.label}
                </option>
              ))}
            </select>
          </div>

          {durationMode === "days" &&
            (useJusSub ? (
              <div className="mb-6">
                <label className="form-label">Duração</label>
                <select
                  className="form-control"
                  value={durationDays}
                  onChange={e => setDurationDays(e.target.value)}
                >
                  <option value="">Selecione...</option>
                  {COURTESY_JUSSUB_DURATION_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="mb-6">
                <label className="form-label">Quantidade de dias</label>
                <input
                  className="form-control"
                  type="number"
                  min="1"
                  step="1"
                  value={durationDays}
                  onChange={e => setDurationDays(e.currentTarget.value)}
                  placeholder="Ex.: 30"
                />
              </div>
            ))}

          {durationMode === "endDate" && (
            <div className="mb-6">
              <label className="form-label">Data de fim</label>
              <input
                className="form-control"
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
              />
              {useJusSub && (
                <small className="form-text text-muted">
                  A data precisa resultar em 1, 7, 14, 30, 60, 90, 180 ou 365
                  dias a partir de hoje.
                </small>
              )}
            </div>
          )}

          {durationMode === "plan" && (
            <p className="text-muted mb-0">
              A cortesia seguirá a duração padrão do plano selecionado.
            </p>
          )}
        </div>

        <div className="alert alert-light border mb-0">
          <strong>Antes de confirmar:</strong>
          <p className="mb-0 mt-2">{confirmationSummary}</p>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <button
          type="button"
          className="btn btn-outline-primary"
          onClick={onClose}
          disabled={isLoading}
        >
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleConfirm}
          disabled={isLoading}
        >
          {isLoading ? "Criando..." : "Criar cortesia"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
